import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { debounce } from 'lodash';
import { sortInitiativeOrder } from '../utils/tokenUtils';
import {
  createDefaultMap,
  createMapFromReactState,
  hydrateSessionFromSource,
  hasMapStateChanged,
  mergeGameStateIntoMap,
  cloneGameStateSnapshot,
  DEFAULT_MAP_STATE,
  mapManagerInitFieldsFromSource,
  GRID_AND_GLORY_LOCAL_STORAGE_KEY,
} from '../utils/gameStateManager';

const LOCAL_STORAGE_KEY = GRID_AND_GLORY_LOCAL_STORAGE_KEY;
const LOCAL_STORAGE_VERSION = '2.0';
const LOCAL_STORAGE_VERSION_KEY = 'gridAndGloryVersion';

const getInitialState = () => {
  const savedVersion = localStorage.getItem(LOCAL_STORAGE_VERSION_KEY);
  
  if (savedVersion && savedVersion !== LOCAL_STORAGE_VERSION) {
    console.log('[Init] - Clearing outdated localStorage (version mismatch):', savedVersion, '->', LOCAL_STORAGE_VERSION);
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    localStorage.setItem(LOCAL_STORAGE_VERSION_KEY, LOCAL_STORAGE_VERSION);
  } else if (!savedVersion) {
    console.log('[Init] - Setting initial version:', LOCAL_STORAGE_VERSION);
    localStorage.setItem(LOCAL_STORAGE_VERSION_KEY, LOCAL_STORAGE_VERSION);
  }
  
  const defaultMap = createDefaultMap({ id: 'default', name: 'Main Map' });
  const savedState = localStorage.getItem(LOCAL_STORAGE_KEY);
  
  if (savedState) {
    try {
      const parsed = JSON.parse(savedState);
      console.log('[Init] - Loaded state from localStorage:', parsed);
      
      const currentMapId = parsed.currentMapId || 'default';
      const viewingMapId = parsed.viewingMapId || currentMapId;
      let maps = parsed.maps || [defaultMap];
      
      if (!maps || maps.length === 0) {
        maps = [defaultMap];
      }
      
      const activeViewMap = maps.find(map => map.id === viewingMapId) || maps[0];
      console.log('[Init] - Active view map found:', activeViewMap.name);

      return {
        ...mapManagerInitFieldsFromSource(activeViewMap),
        initialCurrentMapId: currentMapId,
        initialViewingMapId: viewingMapId,
        initialMaps: maps,
      };
    } catch (e) {
      console.error('[Init] - Error parsing saved state:', e);
    }
  }
  
  console.log('[Init] - No saved state found, using defaults with default map');
  
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
    currentMapId: 'default',
    viewingMapId: 'default',
    maps: [defaultMap]
  }));
  
  return {
    ...mapManagerInitFieldsFromSource(DEFAULT_MAP_STATE),
    initialCurrentMapId: 'default',
    initialViewingMapId: 'default',
    initialMaps: [defaultMap],
  };
};

/**
 * Hook that manages map state, switching, and persistence.
 * @param {object} params
 * @param {object} params.grid - gridWidth/Height, colors, walls, objects, instances, battlemap + setters
 * @param {object} params.tokens - coins, initiative + setters
 * @param {object} params.room - roomId, isRoomCreator
 * @param {object|null} params.socketHook - passed after useSocket exists (map activation)
 */
export const useMapManager = ({ grid, tokens, room, socketHook }) => {
  const {
    gridWidth,
    gridHeight,
    gridColors,
    gridWalls,
    gridObjects,
    gridObjectInstances,
    selectedBattlemapId,
  } = grid;
  const { coins, initiativeOrder } = tokens;
  const { roomId, isRoomCreator } = room;
  const initialState = useMemo(() => getInitialState(), []);
  
  const [maps, setMaps] = useState(initialState.initialMaps);
  const [currentMapId, setCurrentMapId] = useState(initialState.initialCurrentMapId);
  const [viewingMapId, setViewingMapId] = useState(initialState.initialViewingMapId);
  
  const isLoadingMapRef = useRef(false);
  const lastViewedMapIdRef = useRef(viewingMapId);
  const saveToLocalStorageRef = useRef(null);
  const isActivatingFromLocalRef = useRef(false);
  
  // Initialize debounced save function
  useEffect(() => {
    saveToLocalStorageRef.current = debounce((dataToSave) => {
      try {
        console.log('[Map Persistence] - Saving to localStorage (debounced)');
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(dataToSave));
      } catch (e) {
        console.error('[Map Persistence] - Error saving state to localStorage:', e);
      }
    }, 1000);

    const flushPendingSave = () => {
      if (saveToLocalStorageRef.current?.flush) {
        saveToLocalStorageRef.current.flush();
      }
    };
    window.addEventListener('beforeunload', flushPendingSave);
    
    return () => {
      window.removeEventListener('beforeunload', flushPendingSave);
      flushPendingSave();
      if (saveToLocalStorageRef.current) {
        saveToLocalStorageRef.current.cancel();
      }
    };
  }, []);

  const cancelPendingMapPersistence = useCallback(() => {
    const d = saveToLocalStorageRef.current;
    if (d?.cancel) d.cancel();
  }, []);

  const loadMapData = useCallback((mapToView) => {
    if (!mapToView) return;
    
    console.log('[Map Switching] - Loading map:', mapToView.name);
    isLoadingMapRef.current = true;
    
    setTimeout(() => {
      hydrateSessionFromSource(grid.dispatchGameState, mapToView);
      
      setTimeout(() => {
        isLoadingMapRef.current = false;
        console.log('[Map Switching] - Finished loading map:', mapToView.name);
      }, 50);
    }, 0);
  }, [grid.dispatchGameState]);

  // Map state sync effect
  useEffect(() => {
    if (isLoadingMapRef.current || (roomId && !isRoomCreator)) {
      return;
    }

    const currentMapIndex = maps.findIndex(map => map.id === viewingMapId);
    if (currentMapIndex === -1) return;

    const currentMap = maps[currentMapIndex];
    
    const currentGameState = {
      gridWidth,
      gridHeight,
      battlemapId: selectedBattlemapId,
      gridColors,
      gridWalls,
      gridObjects,
      gridObjectInstances,
      coins,
      initiativeOrder
    };
    const hasStateChanged = hasMapStateChanged(currentMap, currentGameState);
      
    if (hasStateChanged) {
      console.log('[Map State] - Actual map state changed, updating maps array');
      
      const updatedMaps = [...maps];
      updatedMaps[currentMapIndex] = createMapFromReactState(currentMap.id, currentMap.name, {
        ...currentMap,
        ...cloneGameStateSnapshot(currentGameState),
      });
      
      setMaps(updatedMaps);
      
      if (saveToLocalStorageRef.current) {
        saveToLocalStorageRef.current({
          currentMapId,
          viewingMapId,
          maps: updatedMaps
        });
      }
    }
  }, [coins, gridColors, gridWalls, gridObjects, gridObjectInstances, gridWidth, gridHeight, initiativeOrder, selectedBattlemapId, viewingMapId, maps, roomId, isRoomCreator, currentMapId]);

  // Map view change effect
  useEffect(() => {
    if (lastViewedMapIdRef.current === viewingMapId) return;
    
    console.log('[Map View Effect] - viewingMapId changed to:', viewingMapId);
    
    if (isLoadingMapRef.current) {
      console.log('[Map View Effect] - Skipping, another map load is in progress');
      return;
    }
    
    if (maps.length > 0) {
      const mapToView = maps.find(map => map.id === viewingMapId);
      if (mapToView) {
        console.log('[Map View Effect] - Loading map data for:', mapToView.name);
        loadMapData(mapToView);
        lastViewedMapIdRef.current = viewingMapId;
      } else {
        console.warn('[Map View Effect] - Map not found in maps array:', viewingMapId);
        
        try {
          const savedState = localStorage.getItem(LOCAL_STORAGE_KEY);
          if (savedState) {
            const parsed = JSON.parse(savedState);
            const storedMaps = parsed.maps || [];
            const storedMapToView = storedMaps.find(map => map.id === viewingMapId);
            
            if (storedMapToView) {
              console.log('[Map View Effect] - Found map in localStorage, updating maps array');
              const updatedMaps = [...maps, storedMapToView];
              setMaps(updatedMaps);
              loadMapData(storedMapToView);
              lastViewedMapIdRef.current = viewingMapId;
              return;
            }
          }
        } catch (e) {
          console.error('[Map View Effect] - Error checking localStorage:', e);
        }
        
        if (maps.length > 0) {
          console.log('[Map View Effect] - Falling back to first available map');
          const fallbackMap = maps[0];
          setViewingMapId(fallbackMap.id);
          loadMapData(fallbackMap);
          lastViewedMapIdRef.current = fallbackMap.id;
        }
      }
    }
  }, [viewingMapId, maps, loadMapData]);

  // Ensure maps state is valid
  useEffect(() => {
    if (!maps || maps.length === 0) {
      console.log('[Maps Check] - Maps array is empty, adding default map');
      
      const defaultMap = createDefaultMap({ id: 'default', name: 'Main Map' });
      setMaps([defaultMap]);
      setCurrentMapId('default');
      setViewingMapId('default');
      
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
        currentMapId: 'default',
        viewingMapId: 'default',
        maps: [defaultMap]
      }));
    }
  }, [maps]);

  // Save maps to localStorage
  useEffect(() => {
    if (!maps || maps.length === 0 || roomId) return;
    
    console.log('[Maps Save] - Saving maps to localStorage');
    
    if (saveToLocalStorageRef.current) {
      saveToLocalStorageRef.current({
        currentMapId,
        viewingMapId,
        maps
      });
    } else {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
        currentMapId,
        viewingMapId,
        maps
      }));
    }
  }, [maps, currentMapId, viewingMapId, roomId]);

  const handleViewMap = useCallback((mapId) => {
    if (viewingMapId === mapId) return;
    
    console.log('[Map View] - Viewing map:', mapId);
    
    const currentViewingMap = maps.find(map => map.id === viewingMapId);
    const updatedMaps = [...maps];
    
    if (currentViewingMap) {
      const viewingMapIndex = updatedMaps.findIndex(map => map.id === viewingMapId);
      if (viewingMapIndex !== -1) {
        updatedMaps[viewingMapIndex] = createMapFromReactState(currentViewingMap.id, currentViewingMap.name, {
          ...currentViewingMap,
          ...cloneGameStateSnapshot({
            gridWidth,
            gridHeight,
            battlemapId: selectedBattlemapId,
            gridColors,
            gridWalls,
            gridObjects,
            gridObjectInstances,
            coins,
            initiativeOrder,
          }),
        });
        
        setMaps(updatedMaps);
        
        const mapToView = updatedMaps.find(map => map.id === mapId);
        if (mapToView) {
          setViewingMapId(mapId);
          loadMapData(mapToView);
          return;
        }
      }
    }
    
    setViewingMapId(mapId);
  }, [viewingMapId, maps, gridWidth, gridHeight, gridColors, gridWalls, gridObjects, gridObjectInstances, coins, initiativeOrder, selectedBattlemapId, loadMapData]);

  const handleActivateMap = useCallback((mapId, coinsToTransfer = [], runtimeSocketHook = socketHook) => {
    if (currentMapId === mapId && viewingMapId === mapId) return;
    
    console.log('[Map Activate] - Activating map for everyone:', mapId);
    
    const updatedMaps = [...maps];
    
    const currentViewingMapIndex = updatedMaps.findIndex(map => map.id === viewingMapId);
    if (currentViewingMapIndex !== -1) {
      updatedMaps[currentViewingMapIndex] = createMapFromReactState(
        updatedMaps[currentViewingMapIndex].id,
        updatedMaps[currentViewingMapIndex].name,
        {
          ...updatedMaps[currentViewingMapIndex],
          ...cloneGameStateSnapshot({
            gridWidth,
            gridHeight,
            battlemapId: selectedBattlemapId,
            gridColors,
            gridWalls,
            gridObjects,
            gridObjectInstances,
            coins,
            initiativeOrder,
          }),
        }
      );
      console.log('[Map Activate] - Saved current map state to maps array, battlemapId:', selectedBattlemapId);
    }
    
    setMaps(updatedMaps);
    
    const mapToActivate = updatedMaps.find(map => map.id === mapId);
    if (mapToActivate) {
      console.log('[Map Activate] - Found map to activate:', mapToActivate);
      
      let updatedMapCoins = [...(mapToActivate.coins || [])];
      let updatedInitiativeOrder = [...(mapToActivate.initiativeOrder || [])];
      
      if (coinsToTransfer && coinsToTransfer.length > 0) {
        console.log('[Map Activate] - Transferring coins:', coinsToTransfer.length);
        
        const positionedCoins = coinsToTransfer.map(coin => ({
          ...coin,
          id: uuidv4(),
          x: undefined,
          y: undefined,
          initiative: coin.initiative,
          removed: true,
          hidden: false
        }));
        
        updatedMapCoins = [...updatedMapCoins, ...positionedCoins];
        
        const mergedInitiativeOrder = [...updatedInitiativeOrder, ...positionedCoins];
        updatedInitiativeOrder = sortInitiativeOrder(mergedInitiativeOrder);
        
        const mapToActivateIndex = updatedMaps.findIndex(map => map.id === mapId);
        if (mapToActivateIndex !== -1) {
          updatedMaps[mapToActivateIndex] = {
            ...updatedMaps[mapToActivateIndex],
            coins: updatedMapCoins,
            initiativeOrder: updatedInitiativeOrder
          };
          setMaps(updatedMaps);
        }
      }
      
      isActivatingFromLocalRef.current = true;
      
      setTimeout(() => {
        setCurrentMapId(mapId);
        setViewingMapId(mapId);
        
        console.log('[Map Activate] - Loading map data locally:', mapToActivate.name);
        hydrateSessionFromSource(
          grid.dispatchGameState,
          mergeGameStateIntoMap(mapToActivate, {
            coins: updatedMapCoins,
            initiativeOrder: updatedInitiativeOrder
          })
        );
        
        if (roomId && runtimeSocketHook) {
          console.log('[Map Activate] - Notifying server of map switch');
          runtimeSocketHook.switchMap(roomId, mapId);
          
          setTimeout(() => {
            console.log('[Map Activate] - Pushing map data to server (gameStatePatched)');
            runtimeSocketHook.patchGameState(roomId, mapId, {
              coins: updatedMapCoins,
              initiativeOrder: updatedInitiativeOrder,
              gridColors: mapToActivate.gridColors || {},
              gridWalls: mapToActivate.gridWalls || {},
              gridObjects: mapToActivate.gridObjects || {},
              gridObjectInstances: mapToActivate.gridObjectInstances || {},
            });
          }, 100);
        } else if (roomId) {
          console.warn('[Map Activate] - Socket hook unavailable, skipping server map switch emit');
        }
      }, 50);
    } else {
      console.warn('[Map Activate] - Map not found in local state, using basic activation');
      setCurrentMapId(mapId);
      setViewingMapId(mapId);
      
      if (roomId && runtimeSocketHook) {
        runtimeSocketHook.switchMap(roomId, mapId);
      } else if (roomId) {
        console.warn('[Map Activate] - Socket hook unavailable, skipping fallback switchMap emit');
      }
    }
  }, [currentMapId, viewingMapId, roomId, socketHook, maps, coins, gridColors, gridWalls, gridObjects, gridObjectInstances, gridWidth, gridHeight, initiativeOrder, selectedBattlemapId, grid.dispatchGameState]);

  return {
    maps,
    setMaps,
    currentMapId,
    setCurrentMapId,
    viewingMapId,
    setViewingMapId,
    handleViewMap,
    handleActivateMap,
    isActivatingFromLocalRef,
    initialState,
    cancelPendingMapPersistence,
  };
};

export { getInitialState, LOCAL_STORAGE_KEY };
